import React, { useEffect, useState } from 'react'

import { TextInput, required, SelectInput, minLength } from 'react-admin'

import { makeStyles } from '@material-ui/core/styles'
import { Typography, Grid, Paper } from '@material-ui/core'

import { ColorPicker, ResourceSelector, Tooltip } from 'components'
import { slugValidate, numberValidate } from 'utils/formValidations'

import { Props } from './types'

const useStyles = makeStyles({
  gridContainer: {
    width: '100%',
    margin: 0,
    padding: 0,
  },
  paperContainer: {
    padding: 16,
  },
})

export const Form: React.FC<Props> = ({ formData }) => {
  const classes = useStyles()
  const [images, setImages] = useState(Array(3))

  useEffect(() => {
    if (formData && formData?.id) {
      setImages(formData.images)
    }
  }, [])

  return (
    <Grid
      container
      direction="row"
      justifyContent="center"
      alignItems="center"
      spacing={2}
      className={classes.gridContainer}
    >
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        <Paper className={classes.paperContainer}>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Typography variant="h5" color="textSecondary" gutterBottom>
              Editar Banner
            </Typography>
            <hr />
          </Grid>

          <Grid container spacing={2} className={classes.gridContainer}>
            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Tooltip title={'banner.order'}>
                  <TextInput
                    fullWidth
                    label="Ordem"
                    source="order"
                    validate={[
                      required('Obrigatório'),
                      numberValidate,
                      minLength(2, 'Mínimo 2 caracteres numéricos'),
                    ]}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Tooltip title={'banner.text_direction'}>
                  <SelectInput
                    fullWidth
                    label="Posição do texto"
                    source="text_direction"
                    choices={[
                      { id: 'left', name: 'Esquerda' },
                      { id: 'right', name: 'Direita' },
                    ]}
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>
            </Grid>
            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography
                  variant="subtitle2"
                  color="textPrimary"
                >{`Formato do Slug: `}</Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  {`Letras minúsculas separadas por hifen, sem espaços, acentos ou caracteres especiais.`}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  gutterBottom
                >
                  {`[nome do autor]-[nome do curso ou série]`}
                </Typography>

                <Tooltip title={'banner.slug'}>
                  <TextInput
                    fullWidth
                    label="Slug"
                    source="slug"
                    validate={[required('Obrigatório'), slugValidate]}
                  />
                </Tooltip>
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <hr />
                <Typography
                  variant="subtitle1"
                  color="textPrimary"
                  gutterBottom
                >{`Imagem de Fundo: `}</Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Banner – Desktop
                </Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={2} xl={2}>
                <Tooltip title={'banner.image_desktop.order'}>
                  <TextInput
                    fullWidth
                    label="Ordem da imagem"
                    source="images[0].order"
                    validate={[
                      required('Obrigatório'),
                      numberValidate,
                      minLength(2, 'Mínimo 2 caracteres numéricos'),
                    ]}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Tooltip title={'banner.image_desktop.alt'}>
                  <TextInput
                    fullWidth
                    label="Informação alternativa"
                    source="images[0].alt"
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography
                  variant="subtitle2"
                  color="textPrimary"
                >{`Imagem horizontal: `}</Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  {`Insira parte do Nome da Imagem para vincular.`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <ResourceSelector
                  fullWidth
                  label="Caminho da imagem"
                  source="images[0].path"
                  resource="cms/content/images"
                  optionValue="path"
                  searchField="path"
                  validate={[required('Obrigatório')]}
                  selectedValue={images?.[0]?.path}
                  choicesFilter={{
                    target: 'type',
                    filter: 'banner',
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Banner – Mobile
                </Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={2} xl={2}>
                <Tooltip title={'banner.image_mobile.order'}>
                  <TextInput
                    fullWidth
                    label="Ordem da imagem"
                    source="images[2].order"
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Tooltip title={'banner.image_mobile.alt'}>
                  <TextInput
                    fullWidth
                    label="Informação alternativa"
                    source="images[2].alt"
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography
                  variant="subtitle2"
                  color="textPrimary"
                >{`Imagem Vertical: `}</Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  {`Insira parte do Nome da Imagem para vincular.`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <ResourceSelector
                  fullWidth
                  label="Caminho da imagem"
                  source="images[2].path"
                  resource="cms/content/images"
                  optionValue="path"
                  searchField="path"
                  validate={[required('Obrigatório')]}
                  selectedValue={images?.[2]?.path}
                  choicesFilter={{
                    target: 'type',
                    filter: 'banner-mobile',
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <hr />
                <Typography
                  variant="subtitle1"
                  color="textPrimary"
                  gutterBottom
                >{`Marcador: `}</Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                <Tooltip title={'banner.mark.type'}>
                  <SelectInput
                    fullWidth
                    label="Tipo"
                    source="stylesheet.mark.type"
                    choices={[
                      { id: 'contained', name: 'Opaco - contained' },
                      { id: 'outlined', name: 'Borda - outlined' },
                    ]}
                    validate={[required('Obrigatório')]}
                    externalLoading={false}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={5} xl={5}>
                <Tooltip title={'banner.mark.text'}>
                  <TextInput
                    fullWidth
                    label="Texto"
                    source="stylesheet_text.mark.text"
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>

              <Grid container spacing={2} className={classes.gridContainer}>
                <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                  <ColorPicker
                    fullWidth
                    label="Cor do texto"
                    source="stylesheet.mark.text_color"
                    validate={[required('Obrigatório')]}
                    readOnly
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                  <ColorPicker
                    fullWidth
                    label="Cor da Borda"
                    source="stylesheet.mark.color"
                    validate={[required('Obrigatório')]}
                    readOnly
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <hr />
                <Typography
                  variant="subtitle1"
                  color="textPrimary"
                  gutterBottom
                >{`Conteúdo: `}</Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Logo do Banner
                </Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={2} xl={2}>
                <Tooltip title={'banner.logo.order'}>
                  <TextInput
                    fullWidth
                    label="Ordem da imagem"
                    source="images[1].order"
                    validate={[
                      required('Obrigatório'),
                      numberValidate,
                      minLength(2, 'Mínimo 2 caracteres numéricos'),
                    ]}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Tooltip title={'banner.logo.alt'}>
                  <TextInput
                    fullWidth
                    label="Informação alternativa"
                    source="images[1].alt"
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  {`Insira parte do Nome da Imagem para vincular.`}
                </Typography>
                <ResourceSelector
                  fullWidth
                  label="Caminho da imagem"
                  source="images[1].path"
                  resource="cms/content/images"
                  optionValue="path"
                  searchField="path"
                  validate={[required('Obrigatório')]}
                  selectedValue={images?.[1]?.path}
                  choicesFilter={{
                    target: 'type',
                    filter: 'logo',
                  }}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Tooltip title={'banner.title'}>
                  <TextInput fullWidth label="Título" source="title" />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <ColorPicker
                  fullWidth
                  label="Cor do título"
                  source="stylesheet.text_color.title"
                  validate={[required('Obrigatório')]}
                  readOnly
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Tooltip title={'banner.subtitle'}>
                  <TextInput fullWidth label="Subtítulo" source="subtitle" />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <ColorPicker
                  fullWidth
                  label="Cor do subtítulo"
                  source="stylesheet.text_color.subtitle"
                  validate={[required('Obrigatório')]}
                  readOnly
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Tooltip title={'banner.description'}>
                  <TextInput
                    fullWidth
                    label="Description"
                    source="description"
                    validate={[required('Obrigatório')]}
                    multiline
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <ColorPicker
                  fullWidth
                  label="Cor da descrição"
                  source="stylesheet.text_color.description"
                  validate={[required('Obrigatório')]}
                  readOnly
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.gridContainer}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <hr />
                <Typography
                  variant="subtitle1"
                  color="textPrimary"
                  gutterBottom
                >{`Botão: `}</Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                <Tooltip title={'banner.button.type'}>
                  <SelectInput
                    fullWidth
                    label="Tipo"
                    source="stylesheet.button.type"
                    choices={[
                      { id: 'contained', name: 'Opaco - contained' },
                      { id: 'outlined', name: 'Borda - outlined' },
                    ]}
                    validate={[required('Obrigatório')]}
                    externalLoading={false}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                <Tooltip title={'banner.button.text'}>
                  <TextInput
                    fullWidth
                    label="Texto"
                    source="stylesheet_text.button.text"
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>

              <Grid
                container
                spacing={2}
                className={classes.gridContainer}
                direction="row"
              >
                <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                  <ColorPicker
                    fullWidth
                    label="Cor de fundo"
                    source="stylesheet.button.color"
                    validate={[required('Obrigatório')]}
                    readOnly
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                  <ColorPicker
                    fullWidth
                    label="Cor do texto"
                    source="stylesheet.button.text_color"
                    validate={[required('Obrigatório')]}
                    readOnly
                  />
                </Grid>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={12} xl={12}>
                <Tooltip title={'banner.button.slug'}>
                  <TextInput
                    fullWidth
                    label="Link URL"
                    source="stylesheet_text.button.slug"
                    validate={[required('Obrigatório')]}
                  />
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  )
}
